import {
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  keys,
  List,
  NavLink,
  Notification,
  Pagination,
  Paper,
  RingProgress,
  SimpleGrid,
  Stack,
  Stepper,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { useDisclosure } from "@mantine/hooks";
import {
  IconActivity,
  IconArrowRight,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleOff,
  IconDownload,
  IconGauge,
  IconHome2,
  IconMessageCircle,
  IconPhoto,
  IconSearch,
  IconSelector,
  IconSettings,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";

import classes from "./TableSort.module.css";

interface RowData {
  name: string;
  email: string;
  company: string;
}

interface ThProps {
  children: React.ReactNode;
  reversed: boolean;
  sorted: boolean;
  onSort: () => void;
}

function Th({ children, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;
  return (
    <Table.Th className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group justify="space-between">
          <Text fw={500} fz="sm">
            {children}
          </Text>
          <Center className={classes.icon}>
            <Icon size={16} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function filterData(data: RowData[], search: string) {
  const query = search.toLowerCase().trim();
  return data.filter((item) =>
    keys(data[0]).some((key) => item[key].toLowerCase().includes(query)),
  );
}

function sortData(
  data: RowData[],
  payload: { sortBy: keyof RowData | null; reversed: boolean; search: string },
) {
  const { sortBy } = payload;

  if (!sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return b[sortBy].localeCompare(a[sortBy]);
      }

      return a[sortBy].localeCompare(b[sortBy]);
    }),
    payload.search,
  );
}

const data = [
  {
    name: "Athena Weissnat",
    company: "Little - Rippin",
    email: "Elouise.Prohaska@yahoo.com",
  },
  {
    name: "Deangelo Runolfsson",
    company: "Greenfelder - Krajcik",
    email: "Kadin_Trantow87@yahoo.com",
  },
  {
    name: "Danny Carter",
    company: "Kohler and Sons",
    email: "Marina3@hotmail.com",
  },
  {
    name: "Trace Tremblay PhD",
    company: "Crona, Aufderhar and Senger",
    email: "Antonina.Pouros@yahoo.com",
  },
  {
    name: "Derek Dibbert",
    company: "Gottlieb LLC",
    email: "Abagail29@hotmail.com",
  },
  {
    name: "Viola Bernhard",
    company: "Funk, Rohan and Kreiger",
    email: "Jamie23@hotmail.com",
  },
  {
    name: "Austin Jacobi",
    company: "Botsford - Corwin",
    email: "Genesis42@yahoo.com",
  },
  {
    name: "Hershel Mosciski",
    company: "Okuneva, Farrell and Kilback",
    email: "Idella.Stehr28@yahoo.com",
  },
  {
    name: "Mylene Ebert",
    company: "Kirlin and Sons",
    email: "Hildegard17@hotmail.com",
  },
  {
    name: "Lou Trantow",
    company: "Parisian - Lemke",
    email: "Hillard.Barrows1@hotmail.com",
  },
  {
    name: "Dariana Weimann",
    company: "Schowalter - Donnelly",
    email: "Colleen80@gmail.com",
  },
  {
    name: "Dr. Christy Herman",
    company: "VonRueden - Labadie",
    email: "Lilyan98@gmail.com",
  },
  {
    name: "Katelin Schuster",
    company: "Jacobson - Smitham",
    email: "Erich_Brekke76@gmail.com",
  },
  {
    name: "Melyna Macejkovic",
    company: "Schuster LLC",
    email: "Kylee4@yahoo.com",
  },
  {
    name: "Pinkie Rice",
    company: "Wolf, Trantow and Zulauf",
    email: "Fiona.Kutch@hotmail.com",
  },
  {
    name: "Brain Kreiger",
    company: "Lueilwitz Group",
    email: "Rico98@hotmail.com",
  },
];

const items = [
  { title: "Debtor Management", href: "#" },
  { title: "Debtors", href: "#" },
  { title: "{debtor-id}", href: "#" },
].map((item, index) => (
  <Anchor href={item.href} key={index}>
    {item.title}
  </Anchor>
));

const tableElements = [
  { position: 6, mass: 12.011, symbol: "C", name: "Carbon" },
  { position: 7, mass: 14.007, symbol: "N", name: "Nitrogen" },
  { position: 39, mass: 88.906, symbol: "Y", name: "Yttrium" },
  { position: 56, mass: 137.33, symbol: "Ba", name: "Barium" },
  { position: 58, mass: 140.12, symbol: "Ce", name: "Cerium" },
];

const MantineComponents = () => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const rows = tableElements.map((element) => (
    <Table.Tr key={element.name}>
      <Table.Td>{element.position}</Table.Td>
      <Table.Td>{element.name}</Table.Td>
      <Table.Td>{element.symbol}</Table.Td>
      <Table.Td>{element.mass}</Table.Td>
    </Table.Tr>
  ));
  const rows2 = tableElements.map((element) => (
    <Table.Tr
      key={element.name}
      bg={
        selectedRows.includes(element.position)
          ? "var(--mantine-color-blue-light)"
          : undefined
      }
    >
      <Table.Td>
        <Checkbox
          aria-label="Select row"
          checked={selectedRows.includes(element.position)}
          onChange={(event) =>
            setSelectedRows(
              event.currentTarget.checked
                ? [...selectedRows, element.position]
                : selectedRows.filter(
                    (position) => position !== element.position,
                  ),
            )
          }
        />
      </Table.Td>
      <Table.Td>{element.position}</Table.Td>
      <Table.Td>{element.name}</Table.Td>
      <Table.Td>{element.symbol}</Table.Td>
      <Table.Td>{element.mass}</Table.Td>
    </Table.Tr>
  ));

  const [search, setSearch] = useState("");
  const [sortedData, setSortedData] = useState(data);
  const [sortBy, setSortBy] = useState<keyof RowData | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const setSorting = (field: keyof RowData) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(data, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    setSortedData(
      sortData(data, { sortBy, reversed: reverseSortDirection, search: value }),
    );
  };

  const rows3 = sortedData.map((row) => (
    <Table.Tr key={row.name}>
      <Table.Td>{row.name}</Table.Td>
      <Table.Td>{row.email}</Table.Td>
      <Table.Td>{row.company}</Table.Td>
    </Table.Tr>
  ));

  const xIcon = <IconX size={20} />;
  const checkIcon = <IconCheck size={20} />;
  const [loading, { toggle }] = useDisclosure();
  const [active, setActive] = useState(1);
  const nextStep = () =>
    setActive((current) => (current < 3 ? current + 1 : current));
  const prevStep = () =>
    setActive((current) => (current > 0 ? current - 1 : current));
  return (
    <>
      <h4>Switch styles</h4> <Switch label="Switch" defaultChecked />
      <Divider
        my="xs"
        size="sm"
        label="Label in the center"
        labelPosition="center"
        color="red.5"
      />
      <h4>Button Styles</h4>
      {/* Add more dashboard content here */}
      <Group>
        <Button>Button</Button>
        <Divider
          my="xs"
          size="sm"
          label="Label in the center"
          labelPosition="center"
          color="red.5"
        />
        <Button
          variant="gradient"
          gradient={{ from: "blue", to: "cyan", deg: 90 }}
        >
          Gradient button
        </Button>
        <Badge variant="filled">Badge</Badge>
      </Group>
      <Group justify="center">
        <Button leftSection={<IconPhoto size={14} />} variant="default">
          Gallery
        </Button>

        <Button rightSection={<IconDownload size={14} />}>Download</Button>

        <Button
          variant="light"
          leftSection={<IconPhoto size={14} />}
          rightSection={<IconArrowRight size={14} />}
        >
          Visit gallery
        </Button>
      </Group>
      <Group>
        <Button loading={loading}>Filled button</Button>
        <Button variant="light" loading={loading}>
          Light button
        </Button>
        <Button variant="outline" loading={loading}>
          Outline button
        </Button>
        <Switch
          styles={{
            track: {
              cursor: "pointer",
            },
            thumb: {
              cursor: "pointer",
            },
          }}
          checked={loading}
          onChange={toggle}
          label="Loading state"
          mt="md"
        />
      </Group>
      <br />
      <Group>
        <Button loading loaderProps={{ type: "dots" }}>
          Loading button
        </Button>
      </Group>
      <h4>Grids: no styles</h4>
      <SimpleGrid
        cols={{ base: 1, sm: 2, lg: 5 }}
        spacing={{ base: 10, sm: "xl" }}
        verticalSpacing={{ base: "md", sm: "xl" }}
      >
        <div>1</div>
        <div>2</div>
        <div>3</div>
        <div>4</div>
        <div>5</div>
      </SimpleGrid>
      <h4>Grids: paper styles</h4>
      <SimpleGrid
        cols={{ base: 1, sm: 2, lg: 5 }}
        spacing={{ base: "sm", sm: "xl" }}
        verticalSpacing={{ base: "md", sm: "xl" }}
      >
        {[1, 2, 3, 4, 5].map((item) => (
          <Paper key={item} shadow="sm" radius="md" p="md" withBorder>
            <Text>Item {item}</Text>
          </Paper>
        ))}
      </SimpleGrid>
      <h4>Stacks</h4>
      <Stack
        h={300}
        bg="var(--mantine-color-body)"
        align="stretch"
        justify="center"
        gap="md"
      >
        <Button variant="default">1</Button>
        <Button variant="default">2</Button>
        <Button variant="default">3</Button>
      </Stack>
      <h4>Breadcrumbs</h4>
      <Breadcrumbs>{items}</Breadcrumbs>
      <Breadcrumbs separator="→" separatorMargin="md" mt="xs">
        {items}
      </Breadcrumbs>
      <h4>Nav Links</h4>
      <NavLink
        href="#required-for-focus"
        label="With icon"
        leftSection={<IconHome2 size={16} stroke={1.5} />}
      />
      <NavLink
        href="#required-for-focus"
        label="With right section"
        leftSection={<IconGauge size={16} stroke={1.5} />}
        rightSection={
          <IconChevronRight
            size={12}
            stroke={1.5}
            className="mantine-rotate-rtl"
          />
        }
      />
      <NavLink
        href="#required-for-focus"
        label="Disabled"
        leftSection={<IconCircleOff size={16} stroke={1.5} />}
        disabled
      />
      <NavLink
        href="#required-for-focus"
        label="With description"
        description="Additional information"
        leftSection={
          <Badge size="xs" color="red" circle>
            3
          </Badge>
        }
      />
      <NavLink
        href="#required-for-focus"
        label="With description"
        description="Additional information"
        leftSection={
          <Badge size="xs" color="red" circle>
            300
          </Badge>
        }
      />
      <NavLink
        href="#required-for-focus"
        label="Active subtle"
        leftSection={<IconActivity size={16} stroke={1.5} />}
        rightSection={
          <IconChevronRight
            size={12}
            stroke={1.5}
            className="mantine-rotate-rtl"
          />
        }
        variant="subtle"
        active
      />
      <NavLink
        href="#required-for-focus"
        label="Active light"
        leftSection={<IconActivity size={16} stroke={1.5} />}
        rightSection={
          <IconChevronRight
            size={12}
            stroke={1.5}
            className="mantine-rotate-rtl"
          />
        }
        active
      />
      <NavLink
        href="#required-for-focus"
        label="Active filled"
        leftSection={<IconActivity size={16} stroke={1.5} />}
        rightSection={
          <IconChevronRight
            size={12}
            stroke={1.5}
            className="mantine-rotate-rtl"
          />
        }
        variant="filled"
        active
      />
      <h4>Paginations</h4>
      <Divider
        my="xs"
        size="xs"
        label="Label in the center"
        labelPosition="center"
        color="red.5"
      />
      <>
        <Text mb="xs">1 sibling (default)</Text>
        <Pagination total={20} siblings={1} value={10} />

        <Text mb="xs" mt="xl">
          2 siblings
        </Text>
        <Pagination total={20} siblings={2} value={10} />

        <Text mb="xs" mt="xl">
          3 siblings
        </Text>
        <Pagination total={20} siblings={3} value={10} />
      </>
      <>
        <h4>Steppers</h4>
        <Stepper active={active} onStepClick={setActive}>
          <Stepper.Step label="First step" description="Create an account">
            Step 1 content: Create an account
          </Stepper.Step>
          <Stepper.Step label="Second step" description="Verify email">
            Step 2 content: Verify email
          </Stepper.Step>
          <Stepper.Step label="Final step" description="Get full access">
            Step 3 content: Get full access
          </Stepper.Step>
          <Stepper.Completed>
            Completed, click back button to get to previous step
          </Stepper.Completed>
        </Stepper>

        <Group justify="center" mt="xl">
          <Button variant="default" onClick={prevStep}>
            Back
          </Button>
          <Button onClick={nextStep}>Next step</Button>
        </Group>
      </>
      <h4>Tabs</h4>
      <Tabs defaultValue="gallery">
        <Tabs.List>
          <Tabs.Tab value="gallery" leftSection={<IconPhoto size={12} />}>
            Gallery
          </Tabs.Tab>
          <Tabs.Tab
            value="messages"
            leftSection={<IconMessageCircle size={12} />}
          >
            Messages
          </Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={12} />}>
            Settings
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="gallery">Gallery tab content</Tabs.Panel>

        <Tabs.Panel value="messages">Messages tab content</Tabs.Panel>

        <Tabs.Panel value="settings">Settings tab content</Tabs.Panel>
      </Tabs>
      <h4>Notifications</h4>
      <>
        <Notification title="We notify you that">
          You are now obligated to give a star to Mantine project on GitHub
        </Notification>
        <Divider
          my="xs"
          size="xs"
          label="Label in the center"
          labelPosition="center"
          color="red.5"
        />
        <Notification icon={xIcon} color="red" title="Bummer!">
          Something went wrong
        </Notification>
        <Notification icon={checkIcon} color="teal" title="All good!" mt="md">
          Everything is fine
        </Notification>
      </>
      <>
        <h4>Ring Progress</h4>
        <RingProgress
          label={
            <Text size="xs" ta="center">
              Application data usage
            </Text>
          }
          sections={[
            { value: 40, color: "cyan" },
            { value: 15, color: "orange" },
            { value: 15, color: "grape" },
          ]}
        />
      </>
      <>
        <h4>Lists</h4>
        <Divider
          my="xs"
          size="xs"
          label="Label in the center"
          labelPosition="center"
          color="red.5"
        />
        <List
          spacing="xs"
          size="sm"
          center
          icon={
            <ThemeIcon color="teal" size={24} radius="xl">
              <IconCircleCheck size={16} />
            </ThemeIcon>
          }
        >
          <List.Item>List item 1</List.Item>
          <List.Item>List item 2</List.Item>
          <List.Item>List item 3</List.Item>
          <List.Item>List item 4</List.Item>

          <List.Item
            icon={
              <ThemeIcon color="blue" size={24} radius="xl">
                <IconCircleDashed size={16} />
              </ThemeIcon>
            }
          >
            List item 5
          </List.Item>
        </List>
      </>
      <Divider
        my="xs"
        size="xs"
        label="Label in the center"
        labelPosition="center"
        color="red.5"
      />
      <Box>
        <h4>Tables</h4>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Element position</Table.Th>
              <Table.Th>Element name</Table.Th>
              <Table.Th>Symbol</Table.Th>
              <Table.Th>Atomic mass</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
        <Divider
          my="xs"
          size="xs"
          label="Label in the center"
          labelPosition="center"
          color="red.5"
        />
        <Table.ScrollContainer minWidth={500} maxHeight={300}>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Element position</Table.Th>
                <Table.Th>Element name</Table.Th>
                <Table.Th>Symbol</Table.Th>
                <Table.Th>Atomic mass</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Divider
          color="red.5"
          my="xs"
          size="xs"
          label="Label in the center"
          labelPosition="center"
        />
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th>Element position</Table.Th>
              <Table.Th>Element name</Table.Th>
              <Table.Th>Symbol</Table.Th>
              <Table.Th>Atomic mass</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows2}</Table.Tbody>
        </Table>
        <Divider
          my="xs"
          size="xs"
          label="Label in the center"
          labelPosition="center"
          color="red.5"
        />
        <Table.ScrollContainer minWidth={500} maxHeight={600}>
          <TextInput
            placeholder="Search by any field"
            mb="md"
            leftSection={<IconSearch size={16} stroke={1.5} />}
            value={search}
            onChange={handleSearchChange}
          />
          <Table
            horizontalSpacing="md"
            verticalSpacing="xs"
            miw={700}
            layout="fixed"
          >
            <Table.Tbody>
              <Table.Tr>
                <Th
                  sorted={sortBy === "name"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("name")}
                >
                  Name
                </Th>
                <Th
                  sorted={sortBy === "email"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("email")}
                >
                  Email
                </Th>
                <Th
                  sorted={sortBy === "company"}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting("company")}
                >
                  Company
                </Th>
              </Table.Tr>
            </Table.Tbody>
            <Table.Tbody>
              {rows3.length > 0 ? (
                rows3
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={Object.keys(data[0]).length}>
                    <Text fw={500} ta="center">
                      Nothing found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Box>
    </>
  );
};
export default MantineComponents;
